/**
 * Dimension Manager - Local Dimension Cache for Shared Docs
 *
 * Handles dimension fields (mmWidth, mmHeight, mmTextHash) that represent local
 * layout/rendering state. These fields depend on fonts, viewport, browser
 * rendering, etc. and may differ between users.
 *
 * BEHAVIOR INTENTIONALLY DIFFERS BY CONTEXT:
 *
 * For MAIN documents (owned by user):
 *   - These ARE synced to CRDT (each client computes its own, but syncing allows
 *     seeing another user's layout if they edit the same document)
 *   - This is the legacy behavior and changing it would be a breaking change
 *
 * For SHARED docs (viewed by recipients):
 *   - These are NOT synced to CRDT - stored in localDimensions cache instead
 *   - The owner's dimensions may not match viewer's rendering (different fonts, etc.)
 *   - Viewers compute and cache dimensions locally for consistent rendering
 *
 * This intentional inconsistency exists because:
 *   1. Shared docs need local dimension computation for accurate rendering
 *   2. Main documents maintain backward compatibility with existing sync behavior
 *   3. Unifying to "never sync" would require verifying all views handle missing dimensions
 */

/**
 * Fields that represent local layout/rendering state.
 * @type {Set<string>}
 */
export const DIMENSION_FIELDS = new Set(['mmWidth', 'mmHeight', 'mmTextHash']);

/**
 * Local dimension cache for shared docs
 * Dimension fields are stored locally without syncing, merged back during Store rebuild
 * @type {Map<string, Object>}
 */
const localDimensions = new Map(); // nodeId -> { mmWidth, mmHeight, mmTextHash }

/**
 * Check if an update only contains dimension fields (no meaningful data changes)
 * @param {Object} updates - The updates to check
 * @returns {boolean}
 */
export function isDimensionOnlyUpdate(updates) {
  const keys = Object.keys(updates);
  return keys.length > 0 && keys.every(key => DIMENSION_FIELDS.has(key));
}

/**
 * Split updates into dimension fields and syncable fields
 * @param {Object} updates - Node updates
 * @returns {{ dimensionUpdates: Object, syncableUpdates: Object }}
 */
export function splitDimensionFields(updates) {
  const dimensionUpdates = {};
  const syncableUpdates = {};

  for (const [key, value] of Object.entries(updates)) {
    if (DIMENSION_FIELDS.has(key)) {
      dimensionUpdates[key] = value;
    } else {
      syncableUpdates[key] = value;
    }
  }

  return { dimensionUpdates, syncableUpdates };
}

/**
 * Store dimension fields locally (for shared docs)
 * @param {string} nodeId - Node ID
 * @param {Object} dimensions - Dimension fields to store
 */
export function storeLocalDimensions(nodeId, dimensions) {
  const current = localDimensions.get(nodeId) || {};
  localDimensions.set(nodeId, { ...current, ...dimensions });
}

/**
 * Merge local dimensions into a tree (recursive)
 * @param {Object} node - Tree node to merge dimensions into
 */
export function mergeLocalDimensionsIntoTree(node) {
  if (!node) return;

  const dims = localDimensions.get(node.id);
  if (dims) {
    Object.assign(node, dims);
  }

  if (node.children) {
    node.children.forEach(mergeLocalDimensionsIntoTree);
  }
}

/**
 * Clear local dimensions for a node and all its descendants
 * Called when nodes are removed to prevent memory leaks
 * @param {string} nodeId - Node ID
 * @param {Function} findNodeFn - Function to find a node by ID (injected to avoid circular dependency)
 */
export function clearLocalDimensionsForNode(nodeId, findNodeFn) {
  // Clear this node's dimensions
  localDimensions.delete(nodeId);

  // Clear descendants' dimensions
  const node = findNodeFn(nodeId);
  if (node?.children) {
    for (const child of node.children) {
      clearLocalDimensionsForNode(child.id, findNodeFn);
    }
  }
}

/**
 * Prune localDimensions for deleted nodes
 * Prevents memory leak from accumulating dimensions for nodes that no longer exist
 * @param {Object} tree - Current tree structure
 */
export function pruneLocalDimensions(tree) {
  if (!tree || localDimensions.size === 0) return;

  // Collect all current node IDs
  const currentIds = new Set();
  function collectIds(node) {
    if (!node) return;
    currentIds.add(node.id);
    if (node.children) {
      node.children.forEach(collectIds);
    }
  }
  collectIds(tree);

  // Remove entries for deleted nodes
  for (const id of localDimensions.keys()) {
    if (!currentIds.has(id)) {
      localDimensions.delete(id);
    }
  }
}

/**
 * Delete a specific dimension entry
 * @param {string} nodeId - Node ID to delete dimensions for
 */
export function deleteLocalDimensions(nodeId) {
  localDimensions.delete(nodeId);
}

/**
 * Clear all local dimensions
 * Used during document destruction or reset
 */
export function clearAllLocalDimensions() {
  localDimensions.clear();
}

/**
 * Get the size of the local dimensions cache
 * @returns {number}
 */
export function getLocalDimensionsSize() {
  return localDimensions.size;
}

/**
 * Clear local dimensions recursively for a node tree
 * Used when stopping shared doc sync
 * @param {Object} node - Node to clear dimensions for
 */
export function clearLocalDimensionsRecursive(node) {
  if (!node) return;

  localDimensions.delete(node.id);

  if (node.children) {
    for (const child of node.children) {
      clearLocalDimensionsRecursive(child);
    }
  }
}
